import { BlockAddress } from '../output/types';
import { BlockType, NewResource, Resource, ResourceType, SongBlockType } from "./types";

type OldResource = {[key: string]: unknown}

export const fixKnownLegacyIssues = (resource: Resource): Resource => {
  let changesMade = false;

  const repairedBlock = {
    ...resource,
    content: resource.content.map(
      (block) => {
        const blockObj = block as OldResource;
        // Some blocks were missing their `type` but had an `originalType`
        // (editor crash when changing resource types perhaps??)
        const newType = blockObj.type || blockObj.originalType;
        if (newType !== blockObj.type) {
          changesMade = true;
        }

        return {
          ...blockObj,
          type: newType,
          content: block.content.map(
            lineGroup => lineGroup.map(
              line => {
                if (typeof line[1] === 'string') {
                  changesMade = true;
                  return [line[0], parseInt(line[1], 10)];
                }
                return line;
              }
            )
          )
        };
      }
    )
  };

  if (changesMade) {
    // Return a different object, so caller knows changes were made
    return repairedBlock as Resource;
  }
  // Return the same object, so caller knows it's identical
  return resource;
};

export const nextGroup = (resource: Resource, address: BlockAddress): BlockAddress => {
  const currentBlock = resource.content[address.block];

  if (currentBlock.content.length > address.group + 1) {
    return { block: address.block, group: address.group + 1, line: 0 };
  }
  else if (resource.content.length > address.block + 1) {
    return { block: address.block + 1, group: 0, line: 0 };
  }
  return address;
};

export const prevGroup = (resource: Resource, address: BlockAddress): BlockAddress => {
  if (address.group > 0) {
    return { block: address.block, group: address.group - 1, line: 0 };
  }
  else if (address.block > 0) {
    return { block: address.block - 1, group: resource.content[address.block - 1].content.length - 1, line: 0 };
  }
  return address;
};

export const seek = (resource: Resource, startAddress: BlockAddress, blockType: BlockType): BlockAddress => {
  const numBlocks = resource.content.length;

  let nextBlock = (startAddress.block + 1) % numBlocks;
  while (nextBlock !== startAddress.block) {
    const candidateBlock = resource.content[nextBlock];
    if (candidateBlock.type === blockType) {
      return { block: nextBlock, group: 0, line: 0 };
    }
    nextBlock = (nextBlock + 1) % numBlocks;
  }

  // We can't find a candidate block: return the address we started with.
  return startAddress;
};

export const seekByIndex = (resource: Resource, blockType: BlockType, targetIndex: number): BlockAddress | undefined => {
  let seen = 0;
  for (const [index, block] of resource.content.entries()) {
    if (block.type === blockType) {
      seen += 1;
      if (seen === targetIndex) {
        return { block: index, group: 0, line: 0 };
      }
    }
  }
};

export const createEmptyResource = (): NewResource => ({
  title: '',
  type: ResourceType.SONG,
  content: [
    {
      type: SongBlockType.VERSE,
      content: [
        [['', 0]]
      ]
    }
  ],
  meta: {
    author: '',
    created: Date.now(),
    modified: Date.now()
  }
});

export const satisfiesCCLI = (resource: Resource): boolean => {
  if (resource.type !== ResourceType.SONG) {
    return true;
  }
  if (resource.meta.licence !== 'CCLI') {
    return true;
  }
  if (
    resource.meta.author && resource.meta.author.trim().length > 0 &&
    resource.meta.copyright && resource.meta.copyright.trim().length > 0
  ) {
    return true;
  }
  return false;
};
